import { useState, useEffect, useMemo } from "react";
import { C, PROGRAMS, PROGRAM_MAP, SURVEY_QUESTIONS, fmtTime } from "./config.js";
import { Btn } from "./common.jsx";

const BASE = "https://parseapi.back4app.com";
const CHART_COLORS = ["#5c7cce","#43b89c","#e67e22","#e74c3c","#9b59b6","#1abc9c","#f39c12","#3498db"];

/* ================================================================
   데이터 로드
   ================================================================ */
const fetchSurvey = async (prog) => {
  if (!prog?.surveyTable) return [];
  try {
    const headers = { "X-Parse-Application-Id": prog.appId, "X-Parse-REST-API-Key": prog.apiKey || prog.restKey || prog.jsKey };
    const res  = await fetch(`${BASE}/classes/${prog.surveyTable}?limit=1000`, { headers });
    const data = await res.json();
    return data.results || [];
  } catch { return []; }
};

const fetchParticipants = async (prog) => {
  if (!prog?.tableId) return [];
  try {
    const headers = { "X-Parse-Application-Id": prog.appId, "X-Parse-REST-API-Key": prog.apiKey || prog.restKey || prog.jsKey };
    const res  = await fetch(`${BASE}/classes/${prog.tableId}?limit=1000`, { headers });
    const data = await res.json();
    return data.results || [];
  } catch { return []; }
};

/* ================================================================
   공통 컴포넌트
   ================================================================ */
function Card({ children, style }) {
  return (
    <div style={{ background:"#fff", border:`1px solid ${C.border}`, borderRadius:12, padding:"20px 22px", ...style }}>
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return <div style={{ fontSize:13, fontWeight:600, color:C.t3, marginBottom:14 }}>{children}</div>;
}

function HBar({ label, count, total, color, pct: forcePct }) {
  const pct = forcePct != null ? forcePct : (total > 0 ? (count / total) * 100 : 0);
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
        <span style={{ fontSize:13, color:C.t1 }}>{label}</span>
        <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>
          {forcePct != null ? `${pct.toFixed(1)}%` : `${count}명`}
          {forcePct == null && <span style={{ color:C.t3, fontWeight:400, marginLeft:4 }}>({pct.toFixed(1)}%)</span>}
        </span>
      </div>
      <div style={{ height:7, background:C.bg, borderRadius:4, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:color, borderRadius:4, transition:"width .8s ease" }}/>
      </div>
    </div>
  );
}

function WordCloud({ texts }) {
  const words = useMemo(() => {
    const stopWords = new Set(["있어요","있었어요","것","더","이","가","은","는","을","를","에","의","도","로","와","과","그","너무","정말","매우","했어요","했습니다","어요","해요","같아요","있습니다","좋았","좋았어요","조금","많이"]);
    const freq = {};
    texts.forEach(text => {
      if (!text) return;
      text.replace(/[^\w가-힣\s]/g, "").split(/\s+/).forEach(w => {
        if (w.length >= 2 && !stopWords.has(w)) freq[w] = (freq[w] || 0) + 1;
      });
    });
    return Object.entries(freq).sort((a,b) => b[1]-a[1]).slice(0, 40).map(([word, count]) => ({ word, count }));
  }, [texts]);

  if (!words.length) return <div style={{ color:C.t3, fontSize:13 }}>데이터가 없습니다.</div>;
  const maxCount = words[0]?.count || 1;
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", lineHeight:1.6 }}>
      {words.map(({ word, count }, i) => {
        const ratio    = count / maxCount;
        const fontSize = Math.round(12 + ratio * 20);
        const color    = CHART_COLORS[i % CHART_COLORS.length];
        return (
          <span key={word} style={{ fontSize, fontWeight:ratio > 0.5 ? 700 : 400, color, opacity:0.5 + ratio * 0.5 }}>{word}</span>
        );
      })}
    </div>
  );
}

function ClearTimeDist({ participants }) {
  const dist = useMemo(() => {
    const buckets = [
      { label:"10분 이하",    min:0,    max:600,      color:CHART_COLORS[0] },
      { label:"10~15분",      min:600,  max:900,      color:CHART_COLORS[1] },
      { label:"15~20분",      min:900,  max:1200,     color:CHART_COLORS[2] },
      { label:"20~25분",      min:1200, max:1500,     color:CHART_COLORS[3] },
      { label:"25~30분 미만", min:1500, max:1800,     color:CHART_COLORS[4] },
      { label:"30분(타임아웃)",min:1800, max:Infinity, color:CHART_COLORS[5] },
    ];
    const times = participants.map(p => p.clearTime || p.clear_time).filter(v => !isNaN(v) && v > 0);
    return buckets.map(b => ({ ...b, count: times.filter(t => t >= b.min && t < b.max).length }));
  }, [participants]);

  return (
    <div>
      {dist.map(d => (
        <HBar key={d.label} label={d.label} count={d.count} total={participants.length} color={d.color}/>
      ))}
    </div>
  );
}

/* ================================================================
   AI 보고서
   ================================================================ */
function AIReport({ prog, surveys, participants, onClose }) {
  const [step,     setStep]     = useState("idle"); // idle | loading | done | error
  const [aiResult, setAiResult] = useState(null);   // { positive:[], negative:[], insights:[] }
  const [errMsg,   setErrMsg]   = useState("");

  /* ── Claude API 호출 ── */
  const generate = async () => {
    setStep("loading");
    setAiResult(null);

    const comments = surveys.map(s => s.q6).filter(Boolean);
    const scoreQs  = SURVEY_QUESTIONS.filter(q => q.type === "score");
    const avgScores = scoreQs.map(q => {
      const vals = surveys.map(s => parseInt(s[q.key])).filter(v => !isNaN(v) && v > 0);
      const avg  = vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : 0;
      return q.label + ": " + avg.toFixed(2) + "점";
    }).join(", ");

    const clearTimes = participants.map(p => p.clearTime || p.clear_time).filter(Boolean);
    const avgClear   = clearTimes.length ? Math.round(clearTimes.reduce((a,b)=>a+b,0)/clearTimes.length) : 0;
    const q1vals     = surveys.map(s => parseInt(s.q1)).filter(v => !isNaN(v) && v > 0);
    const avgSat     = q1vals.length ? (q1vals.reduce((a,b)=>a+b,0)/q1vals.length).toFixed(2) : "0";

    const prompt = `당신은 기업 교육 프로그램 전문 분석가입니다.
아래는 "${prog.name}" 프로그램 데이터입니다.

[참여 현황]
참여자: ${participants.length}명, 설문응답: ${surveys.length}명
설문점수(5점만점): ${avgScores}
평균 만족도: ${avgSat}점, 평균 클리어: ${fmtTime(avgClear)}

[주관식 응답 전체 (q6)]
${comments.map((c,i) => (i+1)+". "+c).join("\n")}

위 데이터를 분석해서 반드시 아래 JSON 형식으로만 응답하세요. 다른 텍스트는 절대 포함하지 마세요:
{
  "positive": ["긍정 응답 1", "긍정 응답 2", "긍정 응답 3", "긍정 응답 4", "긍정 응답 5"],
  "negative": ["개선 요구 응답 1", "개선 요구 응답 2", "개선 요구 응답 3", "개선 요구 응답 4", "개선 요구 응답 5"],
  "insights": [
    { "icon": "✅", "title": "인사이트 제목1", "body": "2~3줄 설명" },
    { "icon": "📈", "title": "인사이트 제목2", "body": "2~3줄 설명" },
    { "icon": "💡", "title": "인사이트 제목3", "body": "2~3줄 설명" },
    { "icon": "⚠️", "title": "개선 포인트",   "body": "2~3줄 설명" }
  ]
}

positive: 가장 긍정적인 주관식 응답 5개 (원문 그대로)
negative: 개선 요구가 담긴 주관식 응답 5개 (원문 그대로)
insights: 전체 데이터를 바탕으로 한 종합 인사이트 4개`;

    try {
      const res  = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, max_tokens: 2000 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "API 오류");

      // JSON 파싱
      const text    = data.text || "";
      const jsonStr = text.match(/\{[\s\S]*\}/)?.[0];
      if (!jsonStr) throw new Error("JSON 파싱 실패");
      const parsed = JSON.parse(jsonStr);
      setAiResult(parsed);
      setStep("done");
    } catch(e) {
      setErrMsg(e.message);
      setStep("error");
    }
  };

  useEffect(() => { generate(); }, []);

  /* ── 새 창 PDF 출력 ── */
  const openPrintWindow = () => {
    const clearTimes  = participants.map(p => p.clearTime || p.clear_time).filter(Boolean);
    const avgClear    = clearTimes.length ? Math.round(clearTimes.reduce((a,b)=>a+b,0)/clearTimes.length) : 0;
    const minClear    = clearTimes.length ? Math.min(...clearTimes) : 0;
    const q1vals      = surveys.map(s => parseInt(s.q1)).filter(v => !isNaN(v) && v > 0);
    const avgSat      = q1vals.length ? q1vals.reduce((a,b)=>a+b,0)/q1vals.length : 0;
    const satPct      = ((avgSat/5)*100).toFixed(1);
    const surveyRate  = participants.length > 0 ? ((surveys.length/participants.length)*100).toFixed(1) : 0;
    const avgScore    = participants.length ? Math.round(participants.reduce((a,p)=>a+(p.score||0),0)/participants.length) : 0;

    const satLabels = ["매우 즐거웠습니다","적당히 즐거웠습니다","평범했습니다","그저 그랬습니다","별로였습니다"];
    const satColors = ["#43b89c","#5c7cce","#f39c12","#e67e22","#e74c3c"];
    const satDist   = [5,4,3,2,1].map((score,i) => ({
      label: satLabels[i],
      count: surveys.filter(s => parseInt(s.q1) === score).length,
      pct:   surveys.length > 0 ? ((surveys.filter(s => parseInt(s.q1) === score).length/surveys.length)*100).toFixed(1) : 0,
      color: satColors[i],
    }));

    const timeBuckets = [
      { label:"10분 이하",     min:0,    max:600,      color:"#43b89c" },
      { label:"10~15분",       min:600,  max:900,      color:"#5c7cce" },
      { label:"15~20분",       min:900,  max:1200,     color:"#43b89c" },
      { label:"20~25분",       min:1200, max:1500,     color:"#43b89c" },
      { label:"25~30분 미만",  min:1500, max:1800,     color:"#43b89c" },
      { label:"30분(타임아웃)", min:1800, max:Infinity, color:"#e74c3c" },
    ].map(b => ({ ...b, count: clearTimes.filter(t => t >= b.min && t < b.max).length }));
    const maxTC      = Math.max(...timeBuckets.map(b => b.count), 1);
    const cleared    = clearTimes.filter(t => t < 1800).length;
    const timeout    = clearTimes.filter(t => t >= 1800).length;
    const totalClear = clearTimes.length || 1;

    const stopWords = new Set(["있어요","있었어요","것","더","이","가","은","는","을","를","에","의","도","로","와","과","그","너무","정말","매우","했어요","했습니다","어요","해요","같아요","좋았","조금","많이"]);
    const freq = {};
    surveys.forEach(s => {
      if (!s.q6) return;
      s.q6.replace(/[^\w가-힣\s]/g,"").split(/\s+/).forEach(w => {
        if (w.length >= 2 && !stopWords.has(w)) freq[w] = (freq[w]||0)+1;
      });
    });
    const words  = Object.entries(freq).sort((a,b) => b[1]-a[1]).slice(0,35);
    const maxW   = words[0]?.[1] || 1;
    const wColors = ["#1a7a5e","#2855a0","#c87000","#c0392b","#7b2fa0","#0e7a5e","#1a6090","#b04000"];

    const q5opts   = ["미션 내용 · 스토리","팀원과의 협동 순간","경쟁 · 점수 체계","새로운 공간과 분위기","진행 방식 · 운영"];
    const q5colors = ["#43b89c","#5c7cce","#f39c12","#e67e22","#9b59b6"];
    const q5dist   = q5opts.map((opt,i) => ({
      label: opt,
      count: surveys.filter(s => s.q5 === opt || (s.q5||"").includes(opt.split("·")[0].trim())).length,
      color: q5colors[i],
    }));
    const maxQ5 = Math.max(...q5dist.map(d => d.count), 1);

    const positive = aiResult?.positive || [];
    const negative = aiResult?.negative || [];
    const insights = aiResult?.insights || [];
    const today    = new Date().toLocaleDateString("ko-KR", { year:"numeric", month:"long", day:"numeric" });

    // SVG 막대 차트 (클리어 시간)
    const barChartW = 340, barChartH = 160, barPad = 36;
    const barW = Math.floor((barChartW - barPad * 2) / timeBuckets.length) - 6;
    const barChart = '<svg width="'+barChartW+'" height="'+barChartH+'" xmlns="http://www.w3.org/2000/svg">'
      + timeBuckets.map((b,i) => {
          const h = maxTC > 0 ? Math.round((b.count/maxTC) * (barChartH-50)) : 0;
          const x = barPad + i*(barW+6);
          const y = barChartH - 20 - h;
          return '<rect x="'+x+'" y="'+y+'" width="'+barW+'" height="'+h+'" rx="4" fill="'+b.color+'"/>'
            + '<text x="'+(x+barW/2)+'" y="'+(barChartH-6)+'" text-anchor="middle" font-size="9" fill="#666">'+b.label.replace("분(타임아웃)","분\n(T/O)").split(" ")[0]+'</text>'
            + (b.count>0?'<text x="'+(x+barW/2)+'" y="'+(y-4)+'" text-anchor="middle" font-size="10" font-weight="700" fill="#333">'+b.count+'</text>':'');
        }).join("")
      + '</svg>';

    // SVG 도넛 차트 (성공 vs 타임아웃)
    const r=60, cx=80, cy=80, circ=2*Math.PI*r;
    const successRatio = cleared/totalClear;
    const successDash  = (successRatio*circ).toFixed(1);
    const donut = '<svg width="160" height="160" xmlns="http://www.w3.org/2000/svg">'
      + '<circle cx="'+cx+'" cy="'+cy+'" r="'+r+'" fill="none" stroke="#e8e8e8" stroke-width="22"/>'
      + '<circle cx="'+cx+'" cy="'+cy+'" r="'+r+'" fill="none" stroke="#43b89c" stroke-width="22"'
      + ' stroke-dasharray="'+successDash+' '+circ+'" stroke-dashoffset="'+circ*0.25+'" stroke-linecap="round"/>'
      + '<circle cx="'+cx+'" cy="'+cy+'" r="'+r+'" fill="none" stroke="#e74c3c" stroke-width="22"'
      + ' stroke-dasharray="'+((1-successRatio)*circ).toFixed(1)+' '+circ+'"'
      + ' stroke-dashoffset="'+(circ*0.25 - successDash)+'" stroke-linecap="round"/>'
      + '<text x="'+cx+'" y="'+(cy-8)+'" text-anchor="middle" font-size="20" font-weight="800" fill="#333">'+Math.round(successRatio*100)+'%</text>'
      + '<text x="'+cx+'" y="'+(cy+12)+'" text-anchor="middle" font-size="10" fill="#666">클리어 성공</text>'
      + '</svg>';

    // 화이트 hbar
    const hbar = (label, pct, color, right) =>
      '<div style="margin-bottom:10px">'
      + '<div style="display:flex;justify-content:space-between;font-size:12px;color:#444;margin-bottom:4px">'
      + '<span>'+label+'</span><span style="font-weight:700;color:#111">'+right+'</span></div>'
      + '<div style="height:7px;background:#f0f0f0;border-radius:4px;overflow:hidden">'
      + '<div style="height:100%;width:'+Math.min(pct,100)+'%;background:'+color+';border-radius:4px"></div>'
      + '</div></div>';

    const html = [
      "<!DOCTYPE html><html lang='ko'><head><meta charset='UTF-8'/>",
      "<title>"+prog.name+" 참여자 만족도 분석</title>",
      "<style>",
      "*{margin:0;padding:0;box-sizing:border-box}",
      "body{font-family:'Apple SD Gothic Neo','Malgun Gothic',sans-serif;background:#f5f5f7;color:#1a1a2e}",
      ".page{max-width:880px;margin:0 auto;padding:40px 28px}",
      ".no-print{text-align:right;margin-bottom:24px}",
      ".no-print button{padding:9px 22px;background:#5c7cce;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}",
      ".hero{background:#1a1a2e;color:#fff;border-radius:20px;text-align:center;padding:40px 32px;margin-bottom:28px}",
      ".hero h1{font-size:28px;font-weight:900;letter-spacing:-1px;margin-bottom:6px}",
      ".hero h1 em{color:#43b89c;font-style:normal}",
      ".hero .sub{font-size:12px;color:rgba(255,255,255,.5);margin-bottom:28px}",
      ".hero-kpis{display:flex;justify-content:center;gap:44px;flex-wrap:wrap}",
      ".hero-kpi-val{font-size:28px;font-weight:900;color:#43b89c;letter-spacing:-1px}",
      ".hero-kpi-label{font-size:11px;color:rgba(255,255,255,.5);margin-top:4px}",
      ".sec{margin-bottom:28px}",
      ".sec-title{font-size:16px;font-weight:700;color:#1a1a2e;margin-bottom:14px;padding-bottom:8px;border-bottom:2px solid #e0e0e0}",
      ".grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px}",
      ".card{background:#fff;border-radius:14px;padding:20px 22px;border:1px solid #e8e8e8;box-shadow:0 2px 8px rgba(0,0,0,.04)}",
      ".card-title{font-size:12px;font-weight:600;color:#888;margin-bottom:14px;letter-spacing:.5px}",
      ".card-sub{font-size:11px;color:#aaa;margin-bottom:12px}",
      ".sat-big{font-size:52px;font-weight:900;color:#43b89c;letter-spacing:-2px;text-align:center;margin:8px 0 16px}",
      ".kpi-row{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:28px}",
      ".kpi-card{background:#fff;border-radius:12px;padding:16px 12px;text-align:center;border:1px solid #e8e8e8;box-shadow:0 2px 8px rgba(0,0,0,.04)}",
      ".kpi-val{font-size:22px;font-weight:800;letter-spacing:-1px}",
      ".kpi-label{font-size:11px;color:#888;margin-top:6px}",
      ".wordcloud{display:flex;flex-wrap:wrap;gap:10px;align-items:center;line-height:1.8;padding:8px 0}",
      ".comment-row{display:flex;gap:10px;padding:10px 14px;background:#f8f9fa;border-radius:8px;margin-bottom:8px;border-left:3px solid #e0e0e0}",
      ".comment-num{font-size:13px;font-weight:800;flex-shrink:0}",
      ".comment-text{font-size:12px;color:#444;line-height:1.6}",
      ".insight-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}",
      ".insight-card{background:#fff;border-radius:12px;padding:18px 20px;border:1px solid #e8e8e8;box-shadow:0 2px 8px rgba(0,0,0,.04)}",
      ".insight-icon{font-size:22px;margin-bottom:8px}",
      ".insight-title{font-size:13px;font-weight:700;color:#1a1a2e;margin-bottom:6px}",
      ".insight-body{font-size:12px;color:#666;line-height:1.7}",
      ".footer{text-align:center;font-size:11px;color:#aaa;margin-top:32px;padding-top:14px;border-top:1px solid #e8e8e8}",
      "@media print{body{background:#fff}.no-print{display:none!important}}",
      "</style></head><body><div class='page'>",

      "<div class='no-print'><button onclick='window.print()'>PDF / 인쇄</button></div>",

      // 헤더
      "<div class='hero'>",
      "<h1>"+prog.name+" <em>참여자 만족도 분석</em></h1>",
      "<p class='sub'>"+today+" 기준</p>",
      "<div class='hero-kpis'>",
      "<div><div class='hero-kpi-val'>"+participants.length+"</div><div class='hero-kpi-label'>참여수</div></div>",
      "<div><div class='hero-kpi-val'>"+surveys.length+"</div><div class='hero-kpi-label'>설문응답</div></div>",
      "<div><div class='hero-kpi-val'>"+surveyRate+"%</div><div class='hero-kpi-label'>설문참여율</div></div>",
      "<div><div class='hero-kpi-val'>"+satPct+"%</div><div class='hero-kpi-label'>만족도</div></div>",
      "<div><div class='hero-kpi-val'>"+fmtTime(avgClear)+"</div><div class='hero-kpi-label'>평균클리어</div></div>",
      "</div></div>",

      // 데이터 개요
      "<div class='sec'><div class='sec-title'>데이터 개요</div>",
      "<div class='kpi-row'>",
      ...[
        { label:"참여수",     val:participants.length+"명",  color:"#5c7cce" },
        { label:"설문응답",   val:surveys.length+"명",       color:"#43b89c" },
        { label:"평균클리어", val:fmtTime(avgClear),         color:"#f39c12" },
        { label:"평균점수",   val:avgScore+"점",             color:"#e74c3c" },
        { label:"최단클리어", val:fmtTime(minClear),         color:"#9b59b6" },
      ].map(d =>
        "<div class='kpi-card'>"
        + "<div class='kpi-val' style='color:"+d.color+"'>"+d.val+"</div>"
        + "<div class='kpi-label'>"+d.label+"</div>"
        + "</div>"
      ),
      "</div></div>",

      // 전반적 만족도 + 인상요소
      "<div class='sec'><div class='sec-title'>전반적 만족도</div>",
      "<div class='grid2'>",
      "<div class='card'><div class='card-title'>만족도 응답 비율</div>",
      "<div class='sat-big'>"+satPct+"%</div>",
      ...satDist.map(d => hbar(d.label, d.pct, d.color, d.pct+"%")),
      "</div>",
      "<div class='card'><div class='card-title'>인상 깊었던 요소</div>",
      ...q5dist.map(d => hbar(d.label, Math.round(d.count/maxQ5*100), d.color, d.count+"명")),
      "</div></div></div>",

      // 클리어 시간 분석 (막대+도넛)
      "<div class='sec'><div class='sec-title'>클리어 시간 분석</div>",
      "<div class='grid2'>",
      "<div class='card'>",
      "<div class='card-title'>클리어 시간 분포 (구간별)</div>",
      "<div class='card-sub'>30분 기준 · 구간별 팀 수</div>",
      "<div style='overflow-x:auto'>"+barChart+"</div>",
      "</div>",
      "<div class='card'>",
      "<div class='card-title'>클리어 성공 vs 타임아웃</div>",
      "<div class='card-sub'>30분 내 클리어 여부</div>",
      "<div style='display:flex;align-items:center;justify-content:center;gap:20px;margin-top:8px'>",
      donut,
      "<div>",
      "<div style='display:flex;align-items:center;gap:6px;margin-bottom:8px'><div style='width:12px;height:12px;border-radius:50%;background:#43b89c'></div><span style='font-size:12px;color:#444'>클리어 성공 (30분 이내)</span></div>",
      "<div style='font-size:20px;font-weight:800;color:#43b89c;margin-bottom:12px'>"+cleared+"팀</div>",
      "<div style='display:flex;align-items:center;gap:6px;margin-bottom:8px'><div style='width:12px;height:12px;border-radius:50%;background:#e74c3c'></div><span style='font-size:12px;color:#444'>타임아웃 (30분)</span></div>",
      "<div style='font-size:20px;font-weight:800;color:#e74c3c'>"+timeout+"팀</div>",
      "</div></div>",
      "</div></div></div>",

      // 워드클라우드
      "<div class='sec'><div class='sec-title'>주요 키워드 워드클라우드</div>",
      "<div class='card'><div class='wordcloud'>",
      ...words.map(([w,c],i) => {
        const r = c/maxW;
        const size = Math.round(13+r*22);
        return "<span style='font-size:"+size+"px;font-weight:"+(r>.5?700:400)+";color:"+wColors[i%wColors.length]+";opacity:"+(0.5+r*0.5).toFixed(2)+"'>"+w+"</span>";
      }),
      "</div></div></div>",

      // 주관식 베스트 10
      "<div class='sec'><div class='sec-title'>주관식 응답 베스트 10</div>",
      "<div class='grid2'>",
      "<div>",
      "<div style='font-size:12px;font-weight:700;color:#43b89c;margin-bottom:10px'>긍정 응답 베스트 5</div>",
      ...positive.slice(0,5).map((c,i) =>
        "<div class='comment-row' style='border-left-color:#43b89c'>"
        + "<span class='comment-num' style='color:#43b89c'>"+(i+1)+"</span>"
        + "<span class='comment-text'>"+c+"</span></div>"
      ),
      "</div>",
      "<div>",
      "<div style='font-size:12px;font-weight:700;color:#e74c3c;margin-bottom:10px'>개선 요구 베스트 5</div>",
      ...negative.slice(0,5).map((c,i) =>
        "<div class='comment-row' style='border-left-color:#e74c3c'>"
        + "<span class='comment-num' style='color:#e74c3c'>"+(i+1)+"</span>"
        + "<span class='comment-text'>"+c+"</span></div>"
      ),
      "</div></div></div>",

      // 종합 인사이트
      insights.length > 0
        ? "<div class='sec'><div class='sec-title'>종합 인사이트</div>"
          + "<div class='insight-grid'>"
          + insights.map(ins =>
              "<div class='insight-card'>"
              + "<div class='insight-icon'>"+ins.icon+"</div>"
              + "<div class='insight-title'>"+ins.title+"</div>"
              + "<div class='insight-body'>"+ins.body+"</div>"
              + "</div>"
            ).join("")
          + "</div></div>"
        : "",

      "<div class='footer'>본 보고서는 Back4App 데이터 기반으로 자동 생성되었습니다.</div>",
      "</div></body></html>",
    ].join("");

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  };



  return (
    <div onClick={e => e.target===e.currentTarget && onClose()}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, backdropFilter:"blur(4px)" }}>
      <div style={{ background:"#1a1a2e", borderRadius:18, width:480, maxWidth:"95vw", boxShadow:"0 24px 70px rgba(0,0,0,.4)", overflow:"hidden", border:"1px solid rgba(255,255,255,.1)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 24px", borderBottom:"1px solid rgba(255,255,255,.1)" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>AI 보고서</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.5)", marginTop:2 }}>{prog.name} · Claude AI 분석</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"rgba(255,255,255,.5)" }}>×</button>
        </div>
        <div style={{ padding:"36px 24px", textAlign:"center" }}>
          {step === "loading" && (
            <>
              <div style={{ fontSize:36, marginBottom:14 }}>⏳</div>
              <div style={{ fontSize:15, fontWeight:600, color:"#fff", marginBottom:6 }}>AI가 데이터를 분석하는 중...</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,.5)" }}>주관식 분류 및 인사이트 생성 중입니다</div>
            </>
          )}
          {step === "done" && (
            <>
              <div style={{ fontSize:36, marginBottom:14 }}>✅</div>
              <div style={{ fontSize:15, fontWeight:700, color:"#fff", marginBottom:6 }}>분석 완료!</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,.5)", marginBottom:24 }}>보고서를 새 창에서 확인하고 PDF로 저장하세요.</div>
              <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                <button onClick={onClose} style={{ padding:"10px 20px", borderRadius:10, border:"1px solid rgba(255,255,255,.2)", background:"transparent", fontSize:13, fontWeight:600, cursor:"pointer", color:"rgba(255,255,255,.7)" }}>닫기</button>
                <button onClick={generate} style={{ padding:"10px 20px", borderRadius:10, border:"1px solid rgba(255,255,255,.2)", background:"transparent", fontSize:13, fontWeight:600, cursor:"pointer", color:"rgba(255,255,255,.7)" }}>재생성</button>
                <button onClick={openPrintWindow} style={{ padding:"10px 24px", borderRadius:10, border:"none", background:"#43b89c", fontSize:13, fontWeight:600, cursor:"pointer", color:"#fff" }}>보고서 열기</button>
              </div>
            </>
          )}
          {step === "error" && (
            <>
              <div style={{ fontSize:36, marginBottom:14 }}>❌</div>
              <div style={{ fontSize:14, color:"#e74c3c", marginBottom:20 }}>{errMsg}</div>
              <button onClick={generate} style={{ padding:"10px 24px", borderRadius:10, border:"none", background:"#5c7cce", fontSize:13, fontWeight:600, cursor:"pointer", color:"#fff" }}>다시 시도</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ================================================================
   프로그램별 보고서
   ================================================================ */
function ProgramReport({ prog }) {
  const [surveys,      setSurveys]      = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showAI,       setShowAI]       = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSurvey(prog), fetchParticipants(prog)]).then(([s, p]) => {
      setSurveys(s);
      setParticipants(p);
      setLoading(false);
    });
  }, [prog.id]);

  const stats = useMemo(() => {
    const clearTimes = participants.map(p => p.clearTime || p.clear_time).filter(v => !isNaN(v) && v > 0);
    const minClear   = clearTimes.length ? Math.min(...clearTimes) : null;
    const avgClear   = clearTimes.length ? Math.round(clearTimes.reduce((a,b)=>a+b,0)/clearTimes.length) : null;
    const q1vals     = surveys.map(s => parseInt(s.q1)).filter(v => !isNaN(v) && v > 0);
    const avgSat     = q1vals.length ? q1vals.reduce((a,b)=>a+b,0)/q1vals.length : 0;
    const satPct     = ((avgSat/5)*100).toFixed(1);
    return {
      total:       participants.length,
      surveyCount: surveys.length,
      surveyRate:  participants.length > 0 ? ((surveys.length/participants.length)*100).toFixed(1) : 0,
      avgClear, minClear, satPct,
    };
  }, [participants, surveys]);

  const satDist = useMemo(() => {
    const labels = ["매우 즐거웠습니다","적당히 즐거웠습니다","평범했습니다","그저 그랬습니다","별로였습니다"];
    return [5,4,3,2,1].map((score, i) => ({
      label: labels[i],
      count: surveys.filter(s => parseInt(s.q1) === score).length,
      color: ["#43b89c","#5c7cce","#f39c12","#e67e22","#e74c3c"][i],
    }));
  }, [surveys]);

  const q5dist = useMemo(() => {
    const opts = ["미션 내용 · 스토리","팀원과의 협동 순간","경쟁 · 점수 체계","새로운 공간과 분위기","진행 방식 · 운영"];
    return opts.map((opt, i) => ({
      label: opt,
      count: surveys.filter(s => s.q5 === opt || (s.q5||"").includes(opt.split("·")[0].trim())).length,
      color: CHART_COLORS[i],
    }));
  }, [surveys]);

  const comments   = useMemo(() => surveys.map(s => s.q6).filter(Boolean), [surveys]);
  const bestComments = useMemo(() => [...comments].sort((a,b) => b.length - a.length).slice(0, 5), [comments]);

  if (loading) return <div style={{ padding:"3rem", textAlign:"center", color:C.t3 }}>불러오는 중…</div>;

  return (
    <div>
      {/* 요약 카드 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10, marginBottom:16 }}>
        {[
          { label:"참여수",    val:`${stats.total}명`,            color:"#5c7cce" },
          { label:"설문응답",  val:`${stats.surveyCount}명`,       color:"#43b89c" },
          { label:"설문참여율",val:`${stats.surveyRate}%`,         color:"#43b89c" },
          { label:"만족도",    val:`${stats.satPct}%`,             color:"#e74c3c" },
          { label:"평균클리어",val:stats.avgClear ? fmtTime(stats.avgClear) : "—", color:"#e67e22" },
          { label:"최단클리어",val:stats.minClear ? fmtTime(stats.minClear) : "—", color:"#9b59b6" },
        ].map(({ label, val, color }) => (
          <Card key={label} style={{ textAlign:"center", padding:"14px 10px" }}>
            <div style={{ fontSize:11, color:C.t3, fontWeight:600, marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:18, fontWeight:800, color, letterSpacing:"-1px" }}>{val}</div>
          </Card>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle>만족도 응답 비율</CardTitle>
          {satDist.map(d => <HBar key={d.label} label={d.label} count={d.count} total={surveys.length} color={d.color}/>)}
          {!surveys.length && <div style={{ color:C.t3, fontSize:13 }}>데이터 없음</div>}
        </Card>
        <Card>
          <CardTitle>클리어 시간 분포</CardTitle>
          <ClearTimeDist participants={participants}/>
        </Card>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle>주요 키워드 워드클라우드</CardTitle>
          <WordCloud texts={comments}/>
        </Card>
        <Card>
          <CardTitle>인상 깊었던 요소</CardTitle>
          {q5dist.map(d => <HBar key={d.label} label={d.label} count={d.count} total={surveys.length} color={d.color}/>)}
          {!surveys.length && <div style={{ color:C.t3, fontSize:13 }}>데이터 없음</div>}
        </Card>
      </div>

      <Card style={{ marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <CardTitle style={{ marginBottom:0 }}>주관식 응답 베스트 5</CardTitle>
          <span style={{ fontSize:12, color:C.t3 }}>총 {comments.length}건</span>
        </div>
        {!bestComments.length && <div style={{ color:C.t3, fontSize:13 }}>데이터 없음</div>}
        {bestComments.map((c, i) => (
          <div key={i} style={{ display:"flex", gap:12, marginBottom:10, padding:"12px 16px", background:C.bg, borderRadius:10 }}>
            <span style={{ fontSize:14, fontWeight:800, color:C.accent, flexShrink:0 }}>{i+1}</span>
            <span style={{ fontSize:13, color:C.t1, lineHeight:1.6 }}>{c}</span>
          </div>
        ))}
      </Card>

      {/* AI 보고서 */}
      <div style={{ padding:"20px 24px", background:C.accentBg, borderRadius:16, border:`1px dashed ${C.accent}`, textAlign:"center" }}>
        <div style={{ fontSize:15, fontWeight:700, color:C.accent, marginBottom:6 }}>AI 보고서</div>
        <div style={{ fontSize:13, color:C.t2, marginBottom:14 }}>Claude AI가 설문 데이터를 분석해 인사이트를 제공합니다</div>
        <Btn onClick={() => setShowAI(true)}>AI 보고서 생성</Btn>
      </div>

      {showAI && (
        <AIReport prog={prog} surveys={surveys} participants={participants} onClose={() => setShowAI(false)}/>
      )}
    </div>
  );
}

/* ================================================================
   ReportPage
   ================================================================ */
export default function ReportPage({ courses }) {
  const [activeProgram, setActiveProgram] = useState(PROGRAMS[0].id);
  const [refreshKey,    setRefreshKey]    = useState(0);
  const prog  = PROGRAM_MAP[activeProgram];
  const total = courses.reduce((s, c) => s + (Number(c.capacity)||0), 0);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ padding:"20px 24px 0", flexShrink:0, borderBottom:`1px solid ${C.border}`, background:"#fff" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:C.t1, letterSpacing:"-.5px" }}>보고서</div>
            <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>총 {courses.length}개 과정 · {total}개 코드</div>
          </div>
          <Btn variant="ghost" onClick={() => setRefreshKey(k => k+1)}>↻ 새로고침</Btn>
        </div>
        <div style={{ display:"flex", gap:6, marginBottom:0 }}>
          {PROGRAMS.map(p => {
            const active = activeProgram === p.id;
            return (
              <button key={p.id} onClick={() => { setActiveProgram(p.id); setRefreshKey(k => k+1); }}
                style={{
                  padding:"8px 18px", borderRadius:"12px 12px 0 0", fontSize:13, fontWeight:600,
                  cursor:"pointer", border:`1px solid ${active ? C.border : "transparent"}`,
                  borderBottom: active ? "1px solid #fff" : `1px solid ${C.border}`,
                  background: active ? "#fff" : "transparent",
                  color:      active ? C.accent : C.t3,
                  marginBottom: active ? "-1px" : 0,
                  transition: "all .15s",
                }}
              >{p.name}</button>
            );
          })}
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"20px 24px 24px", background:C.bg }}>
        {prog && <ProgramReport key={`${activeProgram}-${refreshKey}`} prog={prog}/>}
      </div>
    </div>
  );
}